import React, { useEffect } from 'react';
import styled from 'styled-components';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: string;
}

export const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  maxWidth
}) => {
  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <Overlay onClick={onClose}>
      <ModalPanel
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        $maxWidth={maxWidth}
      >
        <ModalHead>
          <div>
            <ModalTitle>{title}</ModalTitle>
            {subtitle && <ModalSub>{subtitle}</ModalSub>}
          </div>
          <CloseBtn onClick={onClose} aria-label="Close">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </CloseBtn>
        </ModalHead>
        <ModalBody>{children}</ModalBody>
        {footer && <ModalFoot>{footer}</ModalFoot>}
      </ModalPanel>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(43, 61, 84, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 24px;
`;

const ModalPanel = styled.div<{ $maxWidth?: string }>`
  background: var(--white);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  max-width: ${({ $maxWidth }) => $maxWidth || '540px'};
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHead = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 24px 0;
`;

const ModalTitle = styled.h3`
  font-family: var(--font-serif);
  font-size: 20px;
  font-weight: 500;
  color: var(--ink);
  margin: 0 0 4px;
`;

const ModalSub = styled.p`
  font-size: 13px;
  color: var(--ink-muted);
  margin: 0;
  line-height: 1.5;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: var(--ink-faint);
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover {
    color: var(--ink);
  }
`;

const ModalBody = styled.div`
  padding: 20px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ModalFoot = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--line);
`;
